import { Problem } from '@sportik/api-coach/problem';
import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const MAX_ATTEMPTS = 3;

// A second attempt only helps when the first one failed on the way: the request
// never landed, or the server itself broke. A 4xx, or an answer the client
// could not read, comes back the same however often it is asked.
function isRetryable(error: Error) {
  return (
    error instanceof Problem && (error.status === 0 || error.status >= 500)
  );
}

/**
 * Server data lives in this cache (ADR 0007). A rejected query is retried only
 * when retrying can help; a rejected mutation always tells the user.
 *
 * `staleTime` is 5 min so a route's prefetch is not refetched on mount
 * (ADR 0016 § 3); `gcTime` stays at the default.
 */
export function createQueryClient() {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error) => {
        toast.error('That did not go through', { description: error.message });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60_000,
        retry: (failureCount, error) =>
          failureCount < MAX_ATTEMPTS - 1 && isRetryable(error),
      },
    },
  });
}
